import _ from 'lodash';
import Element from './Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ClickableMixin from './mixins/ClickableMixin';
import AnimatableMixin from './mixins/AnimatableMixin';
import mixin from '../privates/utils/mixin';
import * as members from '../privates/privateMembers';
import {emptyStringIfNil} from '../privates/utils';
import idGenerator from '../privates/utils/idGenerator';
import platformUtils from '../privates/platformUtils';
import linkGenerator from '../privates/utils/linkGenerator';
import {validate} from '../privates/validations/validation';
import decode from '../privates/utils/decodeHtmlEntities';
import parser from '@wix/neat-html-parser';

const wixGuard = '<span class="wixGuard">&#8203;</span>';

function buildHtmlFromTagStack(tagStack, textInTheMiddle) {
  let openTags = '';
  let closingTags = '';

  for(const {tagName, props} of tagStack) {
    openTags += props ? `<${tagName} ${props}>` : `<${tagName}>`;
    closingTags = `</${tagName}>${closingTags}`;
  }

  return openTags + textInTheMiddle + closingTags;
}

function insertTextInHtml(html, text) {
  const tagStack = [];
  let stop = false;

  parser.parseFragment(html, {
    onText: () => {
      stop = true;
    },
    onOpenTag: (tag) => {
      if (tag.tagName === 'span' && tag.props === 'class="wixGuard"') {
        stop = true;
      }
      if (!stop) { 
        tagStack.push(tag);
      }
    },
    onClosingTag: () => {
      if (!stop) {
        //if we are here than tag is empty
        tagStack.pop();
      }
    }
  });

  return buildHtmlFromTagStack(tagStack, text);
}

//regex taken from santa/htmlParser
const startTagRegex = `<([-A-Za-z0-9_?:]+)((?:\\s+(?:x:)?[-A-Za-z0-9_]+(?:\\s*=\\s*(?:(?:"[^"]*")|(?:'[^']*')|[^>\\s]+))?)*)\\s*(/?)>`;
const endTagRegex = `<\/([-A-Za-z0-9_?:]+)[^>]*>`;
const endBlockTagRegex = `<\/(h[1-6]|p)[^>]*>`;

function getTarget(str) {
    if (!str) {
        return;
    }
    const res = str.match(/target=(?:['"])(.*)(?:['"])/);
    return res && res[1];
}
function convertToTextComponentData(compId, htmlStr) {
    const links = [];
    const linkRegexp = /<a(.*?)href=['"](.*?)["'](.*?)>(.*?)<\/a>/g;
    const textData = htmlStr.replace(linkRegexp, (matched, g1, urlGroup, g3, linkTextGroup) => {
        const target = getTarget(g1) || getTarget(g3) || undefined;
        const linkData = linkGenerator.convertLinkToModelFormatAndReportIfError(urlGroup, target, 'html', undefined, htmlStr);

        if (_.isNil(linkData)) {
            return linkTextGroup;
        }

        linkData.id = idGenerator.generateId(`${compId}_runtime_`);
        links.push(linkData);
        return `<a${g1}dataquery="#${linkData.id}"${g3}>${linkTextGroup}</a>`;
    });
    return {
        text: textData,
        linkList: links
    };
}

function convertLinkForGetHtml(data = '') {
  const linkRegexp = /<a(.*?)dataquery="#(.*?)"(.*?)>(.*?)<\/a>/g;

  const text = data.text && data.text.replace(linkRegexp, (matched, g1, dataQueryId, g3, linkTextGroup) => {
      const link = _.find(data.linkList, {id: dataQueryId});
      const linkUrl = linkGenerator.convertLinkToUrlAndReportIfError(link, undefined, 'html');
      if (!_.isNil(linkUrl)) {
          return `<a${g1}href="${linkUrl}"${g3}>${linkTextGroup}</a>`;
      }
      return linkTextGroup;
    });
  return text;
}
function removeWixGuard(htmlStr) {
  return htmlStr.replace(/<span class="wixGuard">([^<]*)<\/span>/g, (matched, g1 = '') => {
      if (g1.trim() === '&#8203;') {
        return '';
      }
      return g1;
    });
}
/**
 * @class Text
 * @summary Provides functionality for text elements.
 * @classdesc
 *  `Text` type elements contain text which can be formatted using a subset of
 *  standard HTML tags and CSS styles, as detailed below.
 *
 *  <h3 id="formatting-text">Formatting Text</h3>
 *  <h4 id="the-html-subset-supported">Supported HTML Subset</h4>
 *
 *  The supported HTML tags are:
 *
 *  + `<p>`
 *  + `<h1>`, `<h2>`, `<h3>`, `<h4>`, `<h5>`, and `<h6>`
 *  + `<a>`
 *  + `<span>`
 *  + `<ul>`, `<ol>`, and `<li>`
 *  + `<br>`
 *
 *
 *  <h4 id="built-in-text-themes">Text Theme Translation</h4>
 *
 *  Each text theme in the Editor corresponds to a specific set of HTML and CSS attributes:
 *
 *  + **Site Title** — `<h1>`
 *  + **Page Title** — `<h2>`
 *  + **Huge Heading** — `<h3>`
 *  + **Large Heading** — `<h4>`
 *  + **Basic Heading** — `<h5>`
 *  + **Small Heading** — `<h6>`
 *  + **Paragraph 1** — `<p class="p1">`
 *  + **Paragraph 2** — `<p>` or `<p class="p2">`
 *  + **Paragraph 3** — `<p class="p3">`
 *
 *
 *  <h4 id="inline-styling">Inline Styling</h4>
 *
 *  Text elements support inline styling via the `<span>` HTML element. The element can contain the following attributes:
 *
 *  + `style`: For example, `<span style="font-size: 1.5em"></span>`
 *  + `dir` (text direction): For example, `<span dir="rtl"></span>`
 *
 *
 *  The `style` attribute supports the following properties:
 *
 *  + `font-face`
 *  + `font-size`
 *  + `font-weight`
 *  + `font-style`
 *  + `text-decoration`
 *  + `color`
 *  + `background-color`
 *  + `text-align`
 *  + `margin-left`
 *  + `text-shadow`
 *  + `line-height`
 *  + `letter-spacing`
 *
 *
 *  <h4 id="bullets-and-numbering">Bulleted and Numbered Lists</h4>
 *
 *  Text elements can contain bulleted and numbered lists using standard HTML tags:
 *
 *  + Numbered lists — using `<ol>` and `<li>`
 *  + Bulleted lists — using `<ul>` and `<li>`
 *
 *
 *  <h4 id="links">Links</h4>
 *
 *  You can create links using the standard HTML `<a>` anchor element. Anchor elements support the `href` and `target`
 *  attributes. For example:
 *  <p>`<a href="http://wix.com" target="_blank">Stunning</a>`</p>
 *
 *  The `href` attribute can contain all link types in the [Wix link]($w.LinkableMixin.html#link) format.
 *
 * @see <a href="https://www.wix.com/support/html5/article/customizing-and-saving-text-themes"
 *  target="_blank">Customizing and Saving Text Themes</a>
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.ClickableMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.WRichText
 * @definitionId wysiwyg.viewer.components.WRichText
 * @tagname text
 * @defaultcontent {html}
 */
class Text extends Element {
    /**
     * @member html
     * @label property
     * @syntax
     * get html(): string
     * set html(value: string): void
     * @summary Sets or gets the contents of a text element using standard HTML.
     * @description
     *  Setting the `html` property sets the text and styling of
     *  the text element. You can use a subset of HTML tags and CSS attributes
     *  as described [above](#formatting-text). Any text that the element
     *  already contained is replaced.
     *
     *  Getting the `html` property returns the element's text, including
     *  HTML tags.
     *
     *  The `html` property maps the Editor's built-in text themes and custom
     *  formatting to HTML and CSS [as described here](#built-in-text-themes).
     *
     *  To set or get the plain, unstyled contents of a text element, use the
     *  [`text`](#text) property instead.
     * @snippet [Text-html_get.es6=Get a text element's HTML content]
     * @snippet [Text-html_set.es6=Set a text element's HTML content]
     * @type {external:String}
     * @see [Formatting Text](#formatting-text)
     * @see [text](#text)
     * @memberof $w.Text
     * @instance
     * @attr {external:String}
     */
    get html() {
        const richTextUtils = platformUtils.getRichTextUtils();
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const data = RMI.getData(compId);
        return _.flow([convertLinkForGetHtml, richTextUtils.applyTransformationForGetHtml, removeWixGuard])(data);
    }

  /**
   * @member html
   * @memberof $w.Text
   * @param {external:String} value
   */
    set html(value) {
        const richTextUtils = platformUtils.getRichTextUtils();
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'html',
            dataToValidate: {html: value},
            compName: this.id
        });
        if (!isValid) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        value = richTextUtils.applyTransformationForSetHtml(emptyStringIfNil(value));
        RMI.setData(compId, convertToTextComponentData(compId, value));
        return value;
    }

    /**
     * @member text
     * @label property
     * @syntax
     * get text(): string
     * set text(value: string): void
     * @summary Sets or gets the plain-text contents of a text element.
     * @description
     *  Setting the `text` property sets the plain-text contents of
     *  the text element. Any text that the element already contained is
     *  replaced. The new text is formatted using the formatting that was used
     *  on of the first character of the previous `text` value.
     *
     *  Getting the `text` property returns the plain-text content
     *  of the element, without any HTML tags.
     *
     *  To set or get the HTML contents of a text element, use the
     *  [`html`](#html) property instead.
     * @type {external:String}
     * @default An empty string
     * @snippet [Text-text_get.es6=Get a text element's plain-text content]
     * @snippet [Text-text_set.es6=Set a text element's plain-text content]
     * @see [html](#html)
     * @memberof $w.Text
     * @instance
     * @attr {external:String}
     */
    get text() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const data = RMI.getData(compId);
        const rawText = data.text && _.unescape(removeWixGuard(data.text)
            .replace(/\n/g, '')
            .replace(/<br><\/br>/g, '\n')
            .replace(/<br\s*\/?>/g, '\n')
            .replace(new RegExp(endBlockTagRegex, 'mg'), '\n')
            .replace(new RegExp(endTagRegex, 'mg'), '')
            .replace(new RegExp(startTagRegex, 'mg'), '')
            .trim());
        if (_.isUndefined(rawText)) {
            return '';
        }

        return decode(rawText);
    }

  /**
   * @member text
   * @memberof $w.Text
   * @param {external:String} value
   */
    set text(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'text',
            dataToValidate: {text: value},
            compName: this.id
        });
        if (!isValid) {
            return;
        }

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getFullDataWithOverrides(compId);
        value = value ? _.escape(value).replace(/\n/g, '<br>') : wixGuard;
        compData.text = insertTextInHtml(compData.text, value);

        RMI.setData(compId, compData);
        return compData.text;
    }

    toJSON() {
        const {id, type, global, rendered, html, text} = this;
        return {id, type, global, rendered, html, text};
    }
}

mixin(Text.prototype, HiddenCollapsedMixin.prototype);
mixin(Text.prototype, ClickableMixin.prototype);
mixin(Text.prototype, AnimatableMixin.prototype);

export default Text;
